<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const { agentsList } = useAgentsList(true, { includeAgentBots: true });

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');

const assignedAgent = computed({
  get() {
    const assignee = currentChat.value?.meta?.assignee;
    return (
      assignee && {
        ...assignee,
        assignee_type: currentChat.value?.meta?.assignee_type || 'User',
      }
    );
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    const assigneeType = agent ? agent.assignee_type || 'User' : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value.id,
      assignee: agent,
      assigneeType,
    });
    store
      .dispatch('assignAgent', {
        conversationId: currentChat.value.id,
        agentId,
        assigneeType,
      })
      .then(() => useAlert(t('CONVERSATION.CHANGE_AGENT')));
  },
});

const assignedTeam = computed({
  get() {
    return currentChat.value?.meta?.team;
  },
  set(team) {
    const conversationId = currentChat.value.id;
    const teamId = team ? team.id : 0;
    store.dispatch('setCurrentChatTeam', { team, conversationId });
    store
      .dispatch('assignTeam', { conversationId, teamId })
      .then(() => useAlert(t('CONVERSATION.CHANGE_TEAM')));
  },
});

const teamsList = computed(() => {
  if (assignedTeam.value) {
    return [{ id: 0, name: t('TEAMS_SETTINGS.LIST.NONE') }, ...teams.value];
  }
  return teams.value;
});

const showSelfAssign = computed(() => {
  if (!assignedAgent.value) return true;
  return (
    assignedAgent.value.id !== currentUser.value.id ||
    (assignedAgent.value.assignee_type || 'User') !== 'User'
  );
});

const onSelfAssign = () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = {
    ...rest,
    thumbnail: avatar_url,
    assignee_type: 'User',
  };
};

const onAssignAgent = agent => {
  if (
    assignedAgent.value?.id === agent.id &&
    (assignedAgent.value?.assignee_type || 'User') ===
      (agent.assignee_type || 'User')
  ) {
    assignedAgent.value = null;
  } else {
    assignedAgent.value = agent;
  }
};

const onAssignTeam = team => {
  assignedTeam.value = team?.id === 0 ? null : team;
};
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-2 min-w-0 w-full sm:w-auto"
    data-testid="header-assign-actions"
  >
    <div class="min-w-[140px] max-w-[200px] flex-1 sm:flex-none">
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onAssignAgent"
      />
    </div>
    <div
      v-if="teamsList.length"
      class="min-w-[120px] max-w-[180px] flex-1 sm:flex-none"
    >
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        show-emoji-icon
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onAssignTeam"
      />
    </div>
    <NextButton
      v-if="showSelfAssign"
      xs
      faded
      slate
      icon="i-lucide-user-round-plus"
      :label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @click="onSelfAssign"
    />
  </div>
</template>
